import { Box } from "@mui/material";
import { useEffect, type ReactElement } from "react";
import { selectCurrentCompany } from "../../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../../utils";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import type { Job } from "../../../../interfaces/job.interfaces";
import { JobLayout } from "../../../../jobs-list/jobs-layout/Job.layout";
import { JobEventsPaginatedQuery } from "../../hooks/useJobEventsPaginated";
import { useJobEventsBoard } from "../context";
import { JobEventEmpty } from "./JobEventEmpty";
import { JobEventsPagination } from "./JobEventListPagination";
import { JobEventRow } from "./JobEventRow";

interface Props {
  job: Job;
  eventsQuery: JobEventsPaginatedQuery;
}

const INVOICE_PREVIEW_MODAL_KEY_PREFIX = "InvoicePreview-Job";
const INVOICE_LIST_MODAL_KEY_PREFIX = "InvoiceList-Job";
const NEW_EVENT_WINDOW_MS = 24 * 60 * 60 * 1000;

const isRecentlyCreated = (createdAt?: string) => {
  if (!createdAt) return false;

  const createdAtTimestamp = new Date(createdAt).getTime();
  if (!Number.isFinite(createdAtTimestamp)) return false;

  const age = Date.now() - createdAtTimestamp;
  return age >= 0 && age <= NEW_EVENT_WINDOW_MS;
};

export function JobEventsList({ job, eventsQuery }: Props) {
  const { events, page, totalPages, setPage, isFetching, isPlaceholderData } = eventsQuery;
  const { isCreateInvoiceForMultipleVisitsOpen, isBulkWorkforceOpen } = useJobEventsBoard();

  const isSelectionModeOpen = isCreateInvoiceForMultipleVisitsOpen || isBulkWorkforceOpen;
  const invoicePreviewModalKey = `${INVOICE_PREVIEW_MODAL_KEY_PREFIX}-${job.id}`;
  const invoiceListModalKey = `${INVOICE_LIST_MODAL_KEY_PREFIX}-${job.id}`;

  const currentCompany = useAppSelector(selectCurrentCompany);
  const companyTimeZone = currentCompany?.timeZone || "UTC";

  useEffect(() => {
    const storageKey = `focus-job-event-${job.id}`;
    const eventUidToFocus = sessionStorage.getItem(storageKey);

    if (!eventUidToFocus) return;

    const eventToFocus = events.find((event) => event.eventUid === eventUidToFocus);
    if (!eventToFocus) {
      if (page !== 1) {
        setPage(1);
        return;
      }

      if (isFetching || isPlaceholderData) return;

      sessionStorage.removeItem(storageKey);
      return;
    }

    // Espera a que React renderice la card actualizada; después muéstrala y enfócala una sola vez.
    const animationFrame = requestAnimationFrame(() => {
      const element = document.getElementById(`Dashboard-Events-Modal-${eventToFocus.uid}`);
      if (!element) return;

      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.focus({ preventScroll: true });
      sessionStorage.removeItem(storageKey);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [events, isFetching, isPlaceholderData, job.id, page, setPage]);

  if (!events.length) {
    return <JobEventEmpty />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <BaseModalComponent
        modalKey={invoicePreviewModalKey}
        modalLabel="Invoice Preview"
        defaultSizes={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: "1000px" },
          height: { xs: "95%", sm: "90%", md: "90%", lg: "auto" },
        }}
        preventParentTrigger
      >
        {null}
      </BaseModalComponent>

      <BaseModalComponent
        modalKey={invoiceListModalKey}
        modalLabel="Invoices for this visit"
        defaultSizes={{ width: { xs: "95%", sm: 560 }, height: 400 }}
        preventParentTrigger
      >
        {null}
      </BaseModalComponent>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minHeight: "250px",
        }}
      >
        {events.map((event, eventIndex) => {
          const isNewEvent = isRecentlyCreated(event.createdAt);

          return (
            <Box
              key={event.uid}
              id={`Dashboard-Events-Modal-${event.uid}`}
              tabIndex={-1}
              sx={{
                py: 1,
                borderRadius: 3,
                outline: "none",
              }}
            >
              <JobEventRow
                time={{
                  start: formatDate(event.startDate, companyTimeZone),
                  end: formatDate(event.endDate, companyTimeZone),
                }}
                status={event.status}
                isNew={isNewEvent}
                isFocused={isNewEvent}
                workforce={event.workforce}
                jobUid={job.id}
                jobEventUid={event.uid}
                eventSchedulerUid={job.eventUid}
                clientUid={event.clientUid || job.clientUid}
                scopeOfWorkUid={job.scopeOfWorkUid || event.scopeOfWorkUid}
                billingInvoiceUid={event.billingInvoiceUid}
                invoicePreviewModalKey={invoicePreviewModalKey}
                invoiceListModalKey={invoiceListModalKey}
                job={job}
                availabilityWindow={{
                  startAt: event.startDate,
                  endAt: event.endDate,
                  excludeVisitUid: event.uid,
                }}
                showJobPricing={eventIndex === 0}
                wrapLeftPart={(leftContent: ReactElement) =>
                  isSelectionModeOpen ? (
                    leftContent
                  ) : (
                    <BaseModalComponent
                      modalKey={`Dashboard-Events-Modal-${event.uid}`}
                      modalLabel={event.startDate ? `Visit on ${formatDate(event.startDate, companyTimeZone, "MMMM d, yyyy")}` : `Job ${job.id}`}
                      elementTrigger={leftContent}
                    >
                      <JobLayout job={job} currentEventUid={event.uid} disableDetailsButton={true} />
                    </BaseModalComponent>
                  )
                }
              />
            </Box>
          );
        })}
      </Box>

      {/* PAGINATION */}
      <Box sx={{ mt: "auto" }}>
        <JobEventsPagination page={page} totalPages={totalPages} onChange={setPage} />
      </Box>
    </Box>
  );
}
